import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ArrowUpRight } from "lucide-react";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { JsonLd } from "@/components/JsonLd";
import { INDUSTRIES } from "@/content/industries";

export const Route = createFileRoute("/seo-for/")({
  head: () => buildSeo({
    title: "SEO by Industry | Freelancer Tamal",
    description: "Industry-specific SEO playbooks: SaaS, e-commerce, law firms, healthcare, real estate, and restaurants. The exact strategy I deploy for each vertical.",
    path: "/seo-for",
    ogEyebrow: "SEO by Industry",
    keywords: "SEO by industry, industry SEO consultant, vertical SEO, SaaS SEO, ecommerce SEO, law firm SEO, healthcare SEO",
  }),
  component: SeoForIndex,
});

function SeoForIndex() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "SEO by Industry", path: "/seo-for" }]} />
      <Section headingLevel={1}
        eyebrow="SEO by Industry"
        title={<>The same SEO fundamentals — <span className="text-gradient">tuned to your vertical.</span></>}
        subtitle="Every industry has its own ranking factors, content patterns, and trust signals. Pick yours below for the exact playbook."
      >
        <p className="sr-only">Browse industry-specific SEO playbooks.</p>
      </Section>
      <div className="container-x pb-24 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={ind.slug} delay={(i % 3) * 0.06}>
            <Link
              to="/seo-for/$industry"
              params={{ industry: ind.slug }}
              className="group block rounded-3xl border border-border bg-card p-7 hover:border-primary/50 transition h-full"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">{ind.name}</div>
              <h2 className="font-display text-xl leading-tight">{ind.hero}</h2>
              <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{ind.intro}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm text-primary">
                See playbook <ArrowUpRight size={14} className="group-hover:translate-x-0.5 transition" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
      <JsonLd data={breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "SEO by Industry", path: "/seo-for" },
      ])} />
    </>
  );
}